import {
  Paper,
  Typography,
  Button,
  Collapse,
  Icon,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import clsx from 'clsx';

import { LightTooltip } from 'components';

import { collapsedHeight, useBloc } from './bloc';
import { AreaWrapper, EntityFilterByType } from './components';

export function IndicatorWrapper(): JSX.Element {
  const {
    area,
    info,
    error,
    classes,
    getTestId,
    entityType,
    memoLoading,
    memoIsAbout,
    memoIsSchool,
    memoIsScatter,
    prevButtonRef,
    nextButtonRef,
    memoIndicators,
    indicatorEvent,
    descriptionRef,
    RenderComponent,
    handleChangeArea,
    currentIndicator,
    memoDefineColumns,
    descriptionIsOpen,
    memoNextIsDisabled,
    memoPrevIsDisabled,
    memoShowMoreButton,
    memoCurrentIndicator,
    handleToggleOpenInfo,
    handleChangeIndicator,
    similarSchoolsCompare,
    memoShowSimilarSchools,
    selectedFilterChartType,
    handleChangeFilterChartType,
    updateSimiliarSchoolsCompare,
  } = useBloc();

  if (error) {
    throw error;
  }

  return (
    <>
      {!memoIsSchool && !memoIsScatter && !memoIsAbout && (
        <EntityFilterByType
          onSelectFilter={handleChangeFilterChartType}
          loading={memoLoading}
          selectedEntityType={entityType}
          selectedFilterType={selectedFilterChartType}
        />
      )}
      {memoIsScatter && memoIsSchool && (
        <AreaWrapper
          onChangeArea={handleChangeArea}
          value={area}
          areas={currentIndicator?.filters?.area || undefined}
        />
      )}
      <Paper className={classes.root} elevation={0}>
        {(memoLoading ||
          memoDefineColumns.length >= 2 ||
          memoIsAbout ||
          memoIsScatter) && (
          <div className={classes.titleContainer}>
            <Typography
              className={classes.pageTitle}
              color="textSecondary"
              variant="subtitle1"
            >
              {memoLoading ? (
                <Skeleton width="30%" height={30} animation="wave" />
              ) : (
                <>
                  {memoIsAbout
                    ? 'Sobre a avaliação'
                    : `${info?.name} ${area ? ` em ${area.label}` : ''}`.trim()}
                </>
              )}
            </Typography>
            {memoShowSimilarSchools && !memoIsAbout && (
              <div className={classes.similarSchools}>
                {memoLoading ? (
                  <Skeleton width="140px" height={30} animation="wave" />
                ) : (
                  <LightTooltip
                    title={`Clique para ${
                      similarSchoolsCompare ? 'ocultar' : 'mostrar'
                    } resultados de escolas similares`}
                    placement="top"
                  >
                    <FormControlLabel
                      classes={{
                        root: classes.controlLabelRoot,
                      }}
                      control={
                        <Checkbox
                          checked={similarSchoolsCompare}
                          {...getTestId('similar-checkbox')}
                          onChange={(_, checked) =>
                            updateSimiliarSchoolsCompare(checked)
                          }
                          color="primary"
                        />
                      }
                      label={
                        <Typography
                          {...getTestId('similar-schools-label')}
                          color="textSecondary"
                          variant="subtitle2"
                        >
                          Ver escolas similares
                        </Typography>
                      }
                    />
                  </LightTooltip>
                )}
              </div>
            )}
          </div>
        )}
        <div className={classes.contentContainer}>
          <>
            {(memoLoading ||
              memoDefineColumns.length >= 2 ||
              memoIsAbout ||
              memoIsScatter) && (
              <>
                <Collapse
                  in={descriptionIsOpen}
                  collapsedSize={
                    memoIsAbout ? collapsedHeight + 6 : collapsedHeight
                  }
                >
                  {memoIsAbout && (
                    <Typography
                      className={classes.indicatorEventTitle}
                      variant="h5"
                      {...getTestId('indicator-name')}
                    >
                      {indicatorEvent?.name}
                    </Typography>
                  )}

                  {memoIsAbout ? (
                    <Typography
                      component="div"
                      color="textSecondary"
                      variant="subtitle2"
                      ref={descriptionRef}
                      {...getTestId('indicator-description')}
                      dangerouslySetInnerHTML={{
                        __html: indicatorEvent?.description || 'Sem descrição',
                      }}
                    />
                  ) : (
                    <Typography
                      component="div"
                      color="textSecondary"
                      variant="subtitle2"
                      ref={descriptionRef}
                      {...getTestId('indicator-description')}
                    >
                      {memoLoading ? (
                        <>
                          <Skeleton width="90%" height={30} animation="wave" />
                          <Skeleton width="80%" height={30} animation="wave" />
                        </>
                      ) : (
                        <>{currentIndicator?.description || 'Sem descrição'}</>
                      )}
                    </Typography>
                  )}
                </Collapse>
                {memoShowMoreButton && (
                  <>
                    {memoLoading ? (
                      <Skeleton width={60} height={40} />
                    ) : (
                      <Button
                        color="primary"
                        size="small"
                        className={classes.buttonSeeMore}
                        {...getTestId('show-more-button')}
                        startIcon={
                          <Icon
                            className={clsx(classes.icon, {
                              open: descriptionIsOpen,
                            })}
                          >
                            keyboard_arrow_down
                          </Icon>
                        }
                        onClick={handleToggleOpenInfo}
                      >
                        Ler {descriptionIsOpen ? 'menos' : 'mais'}
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
          </>
          <div className={classes.indicatorRenderContainer}>
            {RenderComponent}
          </div>
          {!!memoIndicators.length && (
            <div className={classes.navigationContainer}>
              <Button
                disableElevation
                className={classes.navigationButton}
                color="primary"
                variant={memoPrevIsDisabled ? 'outlined' : 'contained'}
                startIcon={<Icon>chevron_left</Icon>}
                onClick={handleChangeIndicator('prev')}
                disabled={memoPrevIsDisabled || memoLoading}
                ref={prevButtonRef}
                {...getTestId('indicator-prev-button')}
              >
                Anterior
              </Button>
              <Typography
                className={classes.navigationText}
                color="primary"
                variant="subtitle1"
                {...getTestId('current-indicator-text')}
              >
                {memoCurrentIndicator === 0 ? (
                  <b>Sobre a avaliação</b>
                ) : (
                  <>
                    <b>Indicador</b> {memoCurrentIndicator} de{' '}
                    {memoIndicators.length - 1}
                  </>
                )}
              </Typography>
              <Button
                disableElevation
                className={classes.navigationButton}
                color="primary"
                variant={memoNextIsDisabled ? 'outlined' : 'contained'}
                endIcon={<Icon>chevron_right</Icon>}
                onClick={handleChangeIndicator('next')}
                disabled={memoNextIsDisabled || memoLoading}
                ref={nextButtonRef}
                {...getTestId('indicator-next-button')}
              >
                Próximo
              </Button>
            </div>
          )}
        </div>
      </Paper>
    </>
  );
}
